import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useBankTransactions } from "@/hooks/useBankTransactions";
import { format, parseISO, isValid } from "date-fns";
import { useMemo } from "react";
import { useDashboardRange } from "./DashboardContext";
import { useTransactions } from "@/hooks/useTransactions";
import { useAnonymize, anonymizeCategory, anonymizeAmount } from "@/components/AnonymizeContext";

function isDateInCurrentPeriod(date: string, range: "month" | "year") {
  if (!date) return false;
  
  try {
    const now = new Date();
    const d = new Date(date);
    
    // Check if the date is valid
    if (isNaN(d.getTime())) return false;
    
    if (range === "month") {
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    } else {
      return d.getFullYear() === now.getFullYear();
    }
  } catch (e) {
    console.error("Invalid date:", date, e);
    return false;
  }
}

function groupByDate(txns: any[], range: "month" | "year", anonymize: boolean) {
  const out: Record<string, number> = {};
  // Only consider transactions in range and expense, excluding Investment and Credit Card Payment
  const expenseTxns = txns.filter(t =>
    isDateInCurrentPeriod(t.date, range) &&
    t.type === "Expense" &&
    t.amount < 0 &&
    t.category !== "Investment" &&
    t.category !== "Credit Card Payment"
  );

  expenseTxns.forEach((t, idx) => {
    const absAmount = Math.abs(t.amount);
    const key = t.date;
    out[key] = (out[key] || 0) + absAmount;
  });

  // Get all days in the period
  const expenseDays = [...new Set(expenseTxns.map(t => t.date))].sort();

  // Return formatted data for the chart with safe date formatting
  const result = expenseDays.map((date, idx) => {
    // Safely format the date
    let formattedDate = "Invalid";
    try {
      const parsedDate = parseISO(date);
      if (isValid(parsedDate)) {
        formattedDate = format(parsedDate, "MMM d");
      }
    } catch (e) {
      console.error("Error formatting date:", date, e);
    }
    
    return {
      date: formattedDate,
      spent: out[date] || 0
    };
  });

  return result;
}

export default function SpendingBarChart() {
  const { data: bankTxns = [], isLoading: loadingBank, error: errorBank } = useBankTransactions();
  const { data: appTxns = [], isLoading: loadingApp, error: errorApp } = useTransactions();
  const range = useDashboardRange();
  const { anonymize } = useAnonymize();

  const isLoading = loadingBank || loadingApp;
  const error = errorBank || errorApp;

  // Only include bank transactions with type === "Expense"
  const filteredBankTxns = useMemo(
    () => bankTxns.filter((t) => t.type === "Expense"),
    [bankTxns]
  );

  // Merge expense transactions from both sources and filter to current period, expense type,
  // and exclude Investment and Credit Card Payment categories
  const mergedTxns = useMemo(
    () =>
      [
        ...filteredBankTxns,
        ...appTxns,
      ].filter(
        (t) =>
          isDateInCurrentPeriod(t.date, range) &&
          t.type === "Expense" &&
          t.amount < 0 &&
          t.category !== "Investment" &&
          t.category !== "Credit Card Payment"
      ),
    [filteredBankTxns, appTxns, range]
  );

  const data = useMemo(() => groupByDate(mergedTxns, range, anonymize), [mergedTxns, range, anonymize]);

  if (isLoading) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400 bg-white rounded-xl shadow">
        Loading...
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-64 flex items-center justify-center text-destructive bg-white rounded-xl shadow">
        Error loading bar chart.
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400 bg-white rounded-xl shadow">
        No transactions found for this {range}.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <h3 className="font-semibold text-lg mb-2">Spending Over Time</h3>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" stroke="#8E9196" />
          <YAxis
            stroke="#8E9196"
            tickFormatter={tick =>
              anonymize ? anonymizeAmount(Number(tick)) : `$${tick}`
            }
          />
          <Tooltip
            formatter={(value: number) =>
              anonymize
                ? anonymizeAmount(Number(value))
                : `$${Number(value).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}`
            }
          />
          <Bar dataKey="spent" fill="#9b87f5" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
